import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import type { Template, TemplateType } from '@/types/api.types';

const TYPE_LABEL: Record<TemplateType, string> = {
  text: 'Texto',
  template: 'Template Zenvia',
  file: 'Arquivo',
  contact: 'Contato',
  location: 'Localização',
};

export interface Step3Data {
  name: string;
  template: Template | null;
  recipientMode: 'csv' | 'contacts';
  csvPhones: string[];
  selectedContactIds: string[];
  scheduledAt: string;
}

interface Props {
  data: Step3Data;
  onChange: (patch: Partial<Step3Data>) => void;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export function Step3Review({ data, onChange, onBack, onSubmit, isSubmitting }: Props) {
  const recipientCount =
    data.recipientMode === 'csv' ? data.csvPhones.length : data.selectedContactIds.length;

  const firstRecipient =
    data.recipientMode === 'csv'
      ? data.csvPhones[0]
      : data.selectedContactIds[0] ?? null;

  const textContent =
    data.template?.type === 'text'
      ? ((data.template.content as Record<string, unknown>)['text'] as string | undefined)
      : null;

  const previewText = textContent
    ? textContent.replace(/\{\{(\w+)\}\}/g, (_, name: string) =>
        name === 'nome' || name === 'name' ? (firstRecipient ?? '[contato]') : `[${name}]`
      )
    : null;

  return (
    <div className="space-y-5">
      {/* Summary */}
      <div className="rounded-lg border bg-muted/30 p-4 space-y-3 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Campanha</span>
          <span className="font-medium">{data.name}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-muted-foreground">Template</span>
          <div className="flex items-center gap-2">
            <span className="font-medium">{data.template?.name ?? '—'}</span>
            {data.template && (
              <Badge variant="secondary">{TYPE_LABEL[data.template.type]}</Badge>
            )}
          </div>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Destinatários</span>
          <span className="font-medium">{recipientCount}</span>
        </div>
      </div>

      {/* Message preview */}
      {previewText && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Preview (1º destinatário)
          </p>
          <div className="rounded-lg bg-white px-4 py-3 shadow-sm dark:bg-muted text-sm whitespace-pre-wrap leading-relaxed border">
            {previewText}
          </div>
        </div>
      )}

      {/* Schedule */}
      <div className="space-y-2">
        <Label htmlFor="scheduled-at">Agendar para (opcional)</Label>
        <Input
          id="scheduled-at"
          type="datetime-local"
          value={data.scheduledAt}
          onChange={(e) => onChange({ scheduledAt: e.target.value })}
        />
        <p className="text-xs text-muted-foreground">
          Deixe em branco para criar em status "rascunho" e iniciar manualmente.
        </p>
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} disabled={isSubmitting}>← Voltar</Button>
        <Button onClick={onSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Criando...' : 'Criar campanha'}
        </Button>
      </div>
    </div>
  );
}
